// ─────────────────────────────────────────────────────────────────────────
// AreasResumen.js — columna "Por área" de Mi tablero.
//
// Grilla de tarjetas por área con tres contadores (activos, vencidos, en espera)
// calculados acá desde los frentes ya cargados; no hay llamada extra al servidor.
// Al tocar un área se ve su detalle: en escritorio dentro de la misma columna, en
// móvil en una hoja inferior. La selección la guarda Tablero (y la recuerda).
// ─────────────────────────────────────────────────────────────────────────
import React from "react";
import { AREAS, C, boton, diaDe, FilaFrente, Hoja } from "./componentes";

/** Selección especial: frentes sin movimiento en 14 días (contador de la franja superior). */
export const SIN_MOVIMIENTO = "__sin_movimiento";

const ORDEN_ESTADO = ["esta_semana", "activo", "espera_tercero", "congelado"];
const TEXTO_ESTADO = {
  esta_semana: "Esta semana",
  activo: "Activo",
  espera_tercero: "Esperando a terceros",
  congelado: "Congelado",
};

/** Contadores de un área: activos (activo + esta semana), vencidos y en espera. */
const contar = (lista, hoy) => ({
  activos: lista.filter(f => ["activo", "esta_semana"].includes(f.estado)).length,
  vencidos: lista.filter(f => f.plazo && diaDe(f.plazo) < hoy).length,
  espera: lista.filter(f => f.estado === "espera_tercero").length,
});

// Contador en línea ("2 vencidos"); los rótulos no se parten, la fila envuelve si no caben.
const Numero = ({ n, rotulo, color }) => (
  <span style={{ whiteSpace: "nowrap", fontSize: 12, color: C.suave }}>
    <span style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: "tabular-nums", marginRight: 4, color: n > 0 && color ? color : n > 0 ? C.texto : C.suave }}>{n}</span>
    {rotulo}
  </span>
);

const DetalleArea = ({ titulo, lista, hoy, patch, onVolver, textoVolver, aviso }) => {
  const ordenada = [...lista].sort((a, b) => ORDEN_ESTADO.indexOf(a.estado) - ORDEN_ESTADO.indexOf(b.estado));
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <button style={{ ...boton(), padding: "6px 12px" }} onClick={onVolver}>{textoVolver}</button>
        <div style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>{titulo}</div>
      </div>
      {/* En la hoja móvil el aviso de Tablero queda tapado: el 409 se repite acá, como en FormFrente. */}
      {aviso && <div style={{ color: C.ambar, marginBottom: 10 }}>{aviso}</div>}
      {ordenada.length === 0 && <div style={{ color: C.suave }}>No hay frentes abiertos acá.</div>}
      {ordenada.map(f => (
        <FilaFrente
          key={f._id}
          f={f}
          hoy={hoy}
          extra={<div style={{ fontSize: 12, color: C.suave, marginTop: 6 }}>{TEXTO_ESTADO[f.estado] || f.estado}</div>}
        >
          {f.estado === "activo" && (
            <div style={{ marginTop: 8 }}>
              <button style={boton(C.azul)} onClick={() => patch(f._id, { estado: "esta_semana" })}>Subir a esta semana</button>
            </div>
          )}
          {f.estado === "congelado" && (
            <div style={{ marginTop: 8 }}>
              <button style={boton()} onClick={() => patch(f._id, { estado: "activo" })}>Reactivar</button>
            </div>
          )}
        </FilaFrente>
      ))}
    </div>
  );
};

const AreasResumen = ({ abiertos, sinMovimiento, hoy, seleccion, hojaMovil, esMovil, onSeleccionar, onCerrarHoja, patch, aviso }) => {
  const areas = Object.keys(AREAS)
    .map(a => [a, abiertos.filter(f => f.area === a)])
    .filter(([, l]) => l.length);

  const detalle = seleccion
    ? {
        titulo: seleccion === SIN_MOVIMIENTO ? "Sin movimiento en 14 días" : AREAS[seleccion] || seleccion,
        lista: seleccion === SIN_MOVIMIENTO ? sinMovimiento : abiertos.filter(f => f.area === seleccion),
      }
    : null;

  // Escritorio: el detalle reemplaza la grilla dentro de la columna.
  if (!esMovil && detalle) {
    return <DetalleArea {...detalle} hoy={hoy} patch={patch} onVolver={() => onSeleccionar(null)} textoVolver="← Áreas" />;
  }

  return (
    <div>
      {areas.length === 0 && <div style={{ color: C.suave }}>No hay frentes abiertos.</div>}
      <div className="tab-areas">
        {areas.map(([a, lista]) => {
          const n = contar(lista, hoy);
          const marcada = esMovil && seleccion === a;
          return (
            <button
              key={a}
              className="tap-card t-02"
              onClick={() => onSeleccionar(a)}
              style={{ textAlign: "left", background: C.caja, color: C.texto, border: `1px solid ${marcada ? C.azul : C.borde}`, borderRadius: 12, padding: 12, cursor: "pointer", minWidth: 0 }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, minHeight: 36 }}>{AREAS[a]}</div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "2px 10px", marginTop: 8 }}>
                <Numero n={n.activos} rotulo="activos" />
                <Numero n={n.vencidos} rotulo="vencidos" color={C.rojo} />
                <Numero n={n.espera} rotulo="en espera" color={C.ambar} />
              </div>
            </button>
          );
        })}
      </div>

      {esMovil && hojaMovil && detalle && (
        <Hoja onCerrar={onCerrarHoja}>
          <DetalleArea {...detalle} hoy={hoy} patch={patch} onVolver={onCerrarHoja} textoVolver="Cerrar" aviso={aviso} />
        </Hoja>
      )}
    </div>
  );
};

export default AreasResumen;
